import * as _ from 'lodash';
import { Cesium } from 'app/cesium';
import { ImsObject, ImsObjectResource } from './ims_object';
import { AssetType } from './asset_type';
import { Tag } from './tag';
import { AssetResource } from 'app/shared/resources';
import { randomColor } from 'app/lib/random_color';
import { IpDevice } from 'app/shared/collections/ip_device';
import { ImsIconUrlLookup } from '../icons/ims_icon_url_lookup';
import { SelectionType } from 'app/shared/selection/SelectionType';
import { Integration } from './integration';
import { RFTransmitter } from 'app/views/view_types/network/features/rf_planning/collections/rf_transmitter';
import { InitialLocation } from 'app/shared/utilities/InitialLocation';
import { Farm as Site } from 'app/agriculture/contexts/RequiredStartupProviders/FarmContext/farmSchema';
export const IMS_TYPE__ASSET = 'asset';

interface IAssetResource extends ImsObjectResource {
    name: string;
    note: string;
    type_id: number;
    site_id: number;
    is_deleted: boolean;
    last_updated: string;
    qr_code: string;
    json_data: any;
    tags: Array<number>;
    color: string;
    height: number;
    initial_location: InitialLocation;
    is_legacy_anchor: boolean;
    asset_template_id: number;
    creator_id: number;
}

export class Asset extends ImsObject<IAssetResource> {
    icon: string;
    ipDevices: {
        [x: number]: IpDevice;
    };
    rfTransmitters: {
        [x: number]: RFTransmitter;
    };
    integrations: {
        [x: string]: Integration;
    };
    selectionType: string;
    selected: boolean;
    site: Site;
    tags: {
        [x: number]: Tag;
    };
    type: AssetType;
    new;

    private _uniqueColorString: any;
    private _uniqueCesiumColor: any; //Cesium.Color
    private _labelName: string = null;

    constructor(resource = new AssetResource()) {
        super(IMS_TYPE__ASSET, resource);
        this.type = null;
        this.ipDevices = {};
        this.rfTransmitters = {};
        this.integrations = {};
        this.site = null;
        this.tags = {};

        this.icon = 'eye-open';
        this.selectionType = SelectionType.ASSET;
        this.selected = false; // Referenced by cesium for deciding if selected
    }

    applyResourceChange(resource) {
        super.applyResourceChange(resource);
        // We should never have a situation where an existing resource does not have a colour.
        // But if we do then display it as white.
        this._uniqueColorString = resource.color || (resource.id ? '#ffffff' : randomColor());
        this._uniqueCesiumColor = Cesium.Color.fromCssColorString(this._uniqueColorString);
    }

    get name(): string {
        return this.resource.name;
    }

    set name(newName: string) {
        this.resource.name = newName;
    }

    get labelName() {
        return this._labelName ?? this.name;
    }

    set labelName(value: string) {
        this._labelName = value;
    }

    get note() {
        return this.resource.note;
    }

    set note(note) {
        this.resource.note = note;
    }

    get typeId(): number {
        return this.resource.type_id;
    }

    set typeId(typeId: number) {
        this.resource.type_id = typeId;
    }

    get initialLocation(): InitialLocation {
        //This will only be present the first time creating the asset with location
        return this.resource.initial_location;
    }

    set initialLocation(initialLocation: InitialLocation) {
        this.resource.initial_location = initialLocation;
    }

    // Safe getter for the type name of an asset
    get typeName(): string {
        return this.type ? this.type.name : 'Unknown';
    }

    get typeCesiumColor(): string {
        return this.type ? this.type.cesiumColor : Cesium.Color.WHITE;
    }

    get siteId(): number {
        return this.resource.site_id;
    }

    set siteId(siteId: number) {
        if (this.resource.site_id !== null) {
            this.resource.site_id = siteId;
        } else {
            throw new Error('Site ID already defined');
        }
    }

    get isDeleted(): boolean {
        return this.resource.is_deleted;
    }

    set isDeleted(deleted: boolean) {
        this.resource.is_deleted = deleted;
    }

    get lastUpdated() {
        return this.resource.last_updated;
    }

    get ignoredIpDevice() {
        const keys = Object.keys(this.ipDevices);
        for (let i = 0; i < keys.length; ++i) {
            if (this.ipDevices[keys[i]].ignoredIpDevice) {
                return true;
            }
        }
        return false;
    }

    get qrCode() {
        return this.resource.qr_code;
    }

    set qrCode(newQrCode: string) {
        this.resource.qr_code = newQrCode;
    }

    get color() {
        return this.resource.color;
    }

    set color(newColor: string) {
        this.resource.color = newColor;
    }

    get height() {
        return this.resource.height;
    }

    set height(newHeight: number) {
        this.resource.height = newHeight;
    }

    get assetTemplateId(): number {
        return this.resource.asset_template_id;
    }

    set assetTemplateId(assetTemplateId: number) {
        this.resource.asset_template_id = assetTemplateId;
    }

    get creator_id(): number {
        return this.resource.creator_id;
    }

    /**
     * Need to use this if we want to update the tag array. DONT use this.tag
     * this.tag is used by collections in order to populate the asset ims object.
     * setTagArray is used by clients of Asset to update the underlying resource
     * object AND it will also update the tag dictionary
     * @param tagArray
     */
    setTagArray(tagArray) {
        const tagIdArray = [];
        const tagLookupById = {};
        _.forEach(tagArray, (tag) => {
            tagIdArray.push(tag.id);
            tagLookupById[tag.id] = tag;
        });
        this.resource.tags = tagIdArray;
        this.tags = tagLookupById;
    }

    getTagArray() {
        return _.reduce(
            this.tags,
            (result, tag) => {
                result.push(tag);
                return result;
            },
            [],
        );
    }

    get jsonData() {
        return (this.resource.json_data && this.resource.json_data[this.typeId]) || {};
    }

    set jsonData(json) {
        if (!this.resource.json_data) {
            this.resource.json_data = {};
        }
        this.resource.json_data[this.typeId] = json;
    }

    get uniqueColorString() {
        return this._uniqueColorString;
    }

    get uniqueCesiumColor() {
        return this._uniqueCesiumColor;
    }

    /**
     * Create a copy of the IMSAsset - it is connected to a shallow copy of the underlying
     * resource object. All of the IMSAsset internal state is also shallow copied.
     */
    clone(): Asset {
        const resourceClone = _.clone(this.resource);
        resourceClone.json_data = _.cloneDeep(this.resource.json_data);

        const result = new Asset(resourceClone);
        // type is an IMSObject
        result.type = this.type;
        // site is an IMSObject
        result.site = this.site;
        // the following are data structures
        result.ipDevices = _.clone(this.ipDevices);
        // result.stateValues = _.clone(this.stateValues);
        result.tags = _.clone(this.tags);
        return result;
    }

    diff(asset: Asset) {
        if (asset.constructor === Asset) {
            return [
                'id',
                'note',
                'typeId',
                'name',
                'color',
                'tags',
                'jsonData',
                'qrCode',
                'height',
            ].filter((key) => {
                if (key === 'tags') {
                    return (
                        Object.keys(this.tags).filter((id) => !Object.keys(asset.tags).includes(id))
                            .length !== 0 ||
                        Object.keys(this.tags).length !== Object.keys(asset.tags).length
                    );
                }
                if (key === 'jsonData') {
                    return !_.isEqual(this.jsonData, asset.jsonData);
                }
                return asset[key] !== this[key];
            });
        } else {
            throw new Error('Can only diff with an asset');
        }
    }

    getIpDevicesBySystem(systemId: number): Array<Integration> {
        const ipDevices = [];

        Object.values(this.ipDevices).forEach((ipDevice) => {
            Object.values(ipDevice.integrations).forEach((ipDeviceIntegration) => {
                if (systemId === ipDeviceIntegration.systemId && ipDeviceIntegration.active) {
                    ipDevices.push(ipDevice);
                }
            });
        });

        return ipDevices;
    }

    static toFilterable(asset: Asset): FilterableAsset {
        return assetToFilterableAsset(asset, null);
    }

    get isLegacyAnchor(): boolean {
        return this.resource.is_legacy_anchor;
    }

    set isLegacyAnchor(isLegacyAnchor: boolean) {
        this.resource.is_legacy_anchor = isLegacyAnchor;
    }
}

/**
 * Used to do text filtering on an asset
 */
interface FilterableAsset {
    id: number;
    name;
    typeName;
    ipNames;
    ips;
    macs;
    hosts;
    ipDescs;
    ipTags;
    ipQrCodes;
    ipTypeNames;
    tagNames;
    qrCode;
    desc;
    iconUrl;
    lastUpdated;
    commentCount;
}

/**
 * Converts an asset to a filterable asset. Extracts all of the related items
 * and turns them into strings.
 *
 * @param {Asset} asset
 * @param commentCountsForType
 * @returns {FilterableAsset}
 */
function assetToFilterableAsset(
    asset: Asset,
    commentCountsForType: {
        [id: number]: { count: number; countDeleted: number; countWithAttachments: number };
    } = {},
): FilterableAsset {
    return {
        id: asset.id,
        name: asset.name,
        typeName: asset.type ? asset.type.name : undefined,
        ipNames: Object.values(asset.ipDevices).map((ip: IpDevice) => ip.name),
        ips: Object.values(asset.ipDevices).map((ip: IpDevice) => ip.monitorIP),
        macs: Object.values(asset.ipDevices).map((ip: IpDevice) => ip.mac),
        hosts: Object.values(asset.ipDevices).map((ip: IpDevice) => ip.host),
        ipDescs: Object.values(asset.ipDevices).map((ip: IpDevice) => ip.note),
        ipTags: _.flatten(
            Object.values(asset.ipDevices).map((ip: IpDevice) =>
                Object.values(ip.tags).map((tag) => tag.name),
            ),
        ),
        ipQrCodes: Object.values(asset.ipDevices).map((ip: IpDevice) => ip.qrCode),
        ipTypeNames: Object.values(asset.ipDevices).map((ip: IpDevice) =>
            ip.type ? ip.type.name : undefined,
        ),
        tagNames: Object.values(asset.tags).map((tag) => tag.name),
        qrCode: asset.qrCode || undefined,
        desc: asset.note || undefined,
        iconUrl:
            asset && asset.type && asset.type.image_name
                ? ImsIconUrlLookup[asset.type.image_name]
                : undefined,
        lastUpdated: asset.lastUpdated,
        commentCount:
            commentCountsForType && commentCountsForType[asset.id]
                ? commentCountsForType[asset.id].count
                : undefined,
    };
}
